import { ReactNode, useState } from 'react';
import { useBottomSheet } from '@croquiscom-pvt/zds';

import {
  GetMaximumBenefit_payment_event_list_for_display_item_list as PaymentEvent,
  PaymentNotificationItemList,
  PopupViewType,
  useMaximumBenefit,
  usePopupView,
} from '@domains/pdp-common';

import { PaymentBottomSheet } from '../components/payment/PaymentBottomSheet';
import { PAYMENT_POPUP_VIEW_QUERY_KEY } from '../constants/benefit';
import {
  getEventNameList,
  getPaymentEventBottomSheetMapKey,
  getUniqEventMap,
  initialPaymentBottomSheetInfo,
  initialPaymentEvent,
  initialPaymentEventListForDisplay,
  initialPaymentNotification,
} from '../utils/payment-benefit-function';

const MAX_DISPLAY_EVENT_COUNT = 2;

export interface BottomSheetInfo {
  title: string;
  content: string[];
}

export const usePaymentService = () => {
  const { open: openBottomSheet } = useBottomSheet('custom');
  const { showPopup } = usePopupView();
  const country = 'KR';
  const language = 'ko';
  const { payment_event_list_for_display, payment_notification_list } = useMaximumBenefit();

  const { paymentEventListHigherDiscount } = initialPaymentEvent(payment_event_list_for_display);

  const { paymentGuideNotificationList, paymentEventNotificationList, paymentEventNotificationHigherDiscount } =
    initialPaymentNotification(payment_notification_list);

  const convert_payment_event_list_for_display = initialPaymentEventListForDisplay(payment_event_list_for_display);

  const uniqEventMap = getUniqEventMap(convert_payment_event_list_for_display, paymentEventNotificationList);

  const isOverExposureEventLimit = uniqEventMap.size > MAX_DISPLAY_EVENT_COUNT;

  const higherDiscountAmount = Math.max(paymentEventListHigherDiscount, paymentEventNotificationHigherDiscount);

  const eventNameList = getEventNameList(language, uniqEventMap);

  const eventBottomSheetInfo = initialPaymentBottomSheetInfo(
    language,
    convert_payment_event_list_for_display,
    paymentEventNotificationList,
    paymentGuideNotificationList,
  );

  const eventBottomSheetTotalInfo = Array.from(eventBottomSheetInfo.values());

  const [bottomSheetInfo, setBottomSheetInfo] = useState<BottomSheetInfo[]>([]);
  const [isPaymentSheetActive, setIsPaymentSheetActive] = useState(false);
  const [isInterestFeeSheetActive, setIsInterestFeeSheetActive] = useState(false);

  const closeInterestFeeSheet = () => setIsInterestFeeSheetActive(false);

  const handleInterestFeeSheetClick = (children: ReactNode) => {
    showPopup(PopupViewType.INTEREST_FREE, () => {
      setIsInterestFeeSheetActive(true);
      openBottomSheet({ children });
    });
  };

  const handlePaymentBottomSheetOpen = (event?: PaymentEvent | PaymentNotificationItemList) => {
    const key = event ? getPaymentEventBottomSheetMapKey(language, event) : '';

    const currentBottomSheetInfo = event
      ? [eventBottomSheetInfo.get(key) as BottomSheetInfo]
      : eventBottomSheetTotalInfo;

    setBottomSheetInfo(currentBottomSheetInfo);

    showPopup(
      PopupViewType.PAYMENT_EVENT_INFO,
      () => {
        setIsPaymentSheetActive(true);
        openBottomSheet({ children: <PaymentBottomSheet bottomSheetInfo={currentBottomSheetInfo} /> });
      },
      { [PAYMENT_POPUP_VIEW_QUERY_KEY.payment]: key },
    );
  };

  const closePaymentBottomSheet = () => setIsPaymentSheetActive(false);

  return {
    isHide: country !== 'KR',
    isPaymentSheetActive,
    bottomSheetInfo,
    paymentEventList: convert_payment_event_list_for_display,
    paymentEventNotificationList,
    eventNameList,
    isOverExposureEventLimit,
    isInterestFeeSheetActive,
    higherDiscountAmount,
    openInterestFeeSheet: handleInterestFeeSheetClick,
    closeInterestFeeSheet,
    handlePaymentBottomSheetOpen,
    closePaymentBottomSheet,
  };
};
